/**
 * Auto-forward off the core verify-email screen once verification lands.
 *
 * That screen (core/auth, which the game can't edit) exposes no success hook —
 * it only swaps the text of #verify-status — so watch that node and match the
 * success copy explicitly. Matching the exact string (rather than "any change")
 * is what keeps a failed verification from redirecting.
 *
 * The same blind spot leaves the success state half-written: the heading still
 * reads "Verifying email…" after it succeeded, and the trailing plaque still
 * offers "Back to sign in" while we are forwarding the player into the game.
 * Both are repaired here rather than in core.
 */
import { t } from './i18n';

const SUCCESS = /^email verified/i;

export function attachVerifyRedirect(
  root: ParentNode,
  go: () => Promise<void>,
  delayMs = 2000,
): () => void {
  const status = root.querySelector('#verify-status');
  if (!status) return () => {};

  let timer: ReturnType<typeof setTimeout> | undefined;
  let link: HTMLAnchorElement | null = null;
  let onEnter: ((e: Event) => void) | null = null;

  const obs = new MutationObserver(() => {
    if (!SUCCESS.test(status.textContent?.trim() ?? '')) return;
    obs.disconnect(); // one-shot: never re-fire while we're counting down

    const card = status.closest('.bg-auth-card');
    card?.classList.add('auth-verified');

    const h1 = card?.querySelector('h1');
    if (h1) h1.textContent = t('verifyRedirect.heading');

    const note = document.createElement('p');
    note.className = 'verify-note text-[13px] text-text-dim';
    note.textContent = t('verifyRedirect.note');
    status.after(note);

    // "Back to sign in" contradicts the forward we are about to do. Make the
    // plaque the way in instead, and let it skip the countdown.
    link = card?.querySelector<HTMLAnchorElement>('p:last-child a') ?? null;
    if (link) {
      link.textContent = t('verifyRedirect.enter');
      link.href = '/play';
      onEnter = (e) => {
        e.preventDefault();
        if (timer) clearTimeout(timer);
        void go();
      };
      link.addEventListener('click', onEnter);
    }

    timer = setTimeout(() => void go(), delayMs);
  });
  obs.observe(status, { childList: true, characterData: true, subtree: true });

  return () => {
    obs.disconnect();
    if (timer) clearTimeout(timer);
    if (link && onEnter) link.removeEventListener('click', onEnter);
  };
}
